import { useCallback, useMemo } from 'react';
import { get } from 'lodash';
import { useUserPreference } from 'store/userPreference/selectors';
import { af_grade } from 'utils/methods/math';
import { captureException } from '@sentry/react';


const QUESTION_TYPES = {
    MULTIPLE_CHOICE: 'multipleChoice',
    ESSAY: 'essay',
    EMAIL: 'email',
    AUDIO: 'audio',
    VIDEO: 'video',
    FILLING: 'filling',
    PICTURE: 'picture',
    TWO_MINT_AUDIO: 'twoMinAudio',
    FILLINGGIMAGE: 'fillingImage',
    TRUE_FALSE: 'trueFalse',
    MATCH: 'match',
    AUDIOESSAY: 'audioEssay',
    AUDIO_TRUE_FALSE: 'audioTrueFalse',
    AUDIO_MULTI_CHOICE: 'audioMultipleChoice',
    PHTOCOLLAGE: 'photoCollage',
    AUDIO_TO_IMAGE: "audioToImage",
    MULTIPLE_RESPONSE: "multipleResponse",
};

export const useGradeBook = (submissionData) => {
  const userPreference = useUserPreference();
  const isRetakingAvailable = useMemo(() => {
    if (!submissionData) return false;
    const exceptionStatus = get(submissionData, 'exceptionStatus');
    const submissions = get(submissionData, 'submissions');
    const retakes = get(submissionData, 'retakes');
    if (exceptionStatus == 3) {
      return true;
    }
    if (submissions < retakes + 1) {
      return true;
    }
    return false;
  }, [submissionData])

  const getGradeWithPreference = useCallback(
    (submissionData, isPointSystem = false) => {
      const scale_1_5 = userPreference?.gradePref === 'scale_1_5';
      const gradebook_a_f = userPreference?.gradePref === 'gradebook_a_f';

      let grade = isPointSystem ? get(submissionData, 'face_grade') : get(submissionData, 'grade');

      if (scale_1_5 && grade != null) {
        if (!isNaN(grade)) {
          grade = Math.min(4, Math.floor(Math.max(0, grade - 1) / 20)).toString();
        }
      } else if (gradebook_a_f && grade != null) {
        if (!isNaN(grade)) {
          grade = af_grade(grade);
        } else {
          grade = '-';
        }
      }

      return grade;
    },
    [userPreference?.gradePref],
  );

  const checkIsRetaking = (submissionData) => {
    if (!submissionData) return false;
    const retaking = get(submissionData, 'retaking', false);
    return !!retaking;
  };

  const isDoneLateSubmission = (submissionData) => {
    const isLate = get(submissionData, 'isLate');
    const ungraded = get(submissionData, 'ungraded');
    const completed = get(submissionData, 'complete');
    if (isLate && !ungraded && completed) {
      return true;
    }
    return false;
  }

  const getSubmissionStatus = (submissionData) => {
    try {
      const complete = get(submissionData, 'complete');
      const sub_answers = get(submissionData, 'answers', []);
      const answersLength = get(sub_answers, 'length', 0);
      const questionsLength = get(submissionData, 'assignment.questions.length', 0);
      const submissions = get(submissionData, 'submissions');
      const retaking = get(submissionData, 'retaking');
      const assignmentType = get(submissionData, 'assignmentType', get(submissionData, 'type'));
      const isWorksheet = assignmentType === 'worksheet';
      const isGraphicOrganizer = get(submissionData, 'assignment.activity_type') === 'graphic_organizer';
      const hasAnswers = Array.isArray(sub_answers)
        ? answersLength > 0
        : Object.keys(sub_answers || {}).length > 0;
      const isGraphicOrgInProgress = isGraphicOrganizer && !complete && retaking && hasAnswers;
      const submissionStatus =
        submissions == 0 &&
        answersLength == 0 &&
        !(submissionData?.graded && !submissionData?.isTemp) && !isGraphicOrgInProgress;
      let inProgressStatus = false;

      if(!!submissionData && !complete){
        if(isGraphicOrgInProgress){
          inProgressStatus = true;
        }
        if(isWorksheet && get(submissionData, "id")){
          inProgressStatus = true;
        }
        if(submissionData?.id && (!questionsLength || questionsLength >= answersLength)){
          inProgressStatus = true;
        }
      }

      return {
        submissionStatus,
        inProgressStatus,
      };
    } catch (error) {
      captureException(`578 Error in timmerStatus: ${error}`);
      console.log(error);
      return {
        submissionStatus: false,
        inProgressStatus: false,
      };
    }
  };

  const getMarkStatus = (isUngraded, current_submission, usrAssignments) => {
    if (isUngraded) {
      return getSubmissionStatus(usrAssignments).inProgressStatus ? "-" : (current_submission > 0 ? "Credit" : "-")
    }
    return '';
  }
  const getMarkStatusBingo = (isUngraded, current_submission, userAssignment) => {
    if (isUngraded) {
      const isSubmitted = userAssignment?.answers?.length > 0 ? userAssignment?.answers?.some((el) => el?.is_submitted) : false;
      return getSubmissionStatus(userAssignment).inProgressStatus ? "-" : (current_submission > 0 && isSubmitted ? "Credit" : "-")
    }
    return '';
  }

  const typeCheckQuestion = (questionData,ai_feedback=true) => {
    if(ai_feedback) {
      return [QUESTION_TYPES.MULTIPLE_CHOICE, QUESTION_TYPES.AUDIO_MULTI_CHOICE, QUESTION_TYPES.TRUE_FALSE, QUESTION_TYPES.AUDIO_TRUE_FALSE, QUESTION_TYPES.MULTIPLE_RESPONSE, QUESTION_TYPES.MATCH]?.includes(questionData)
    }
    if (Array.isArray(questionData) && questionData.length > 0) {
      return questionData.every((question) => [QUESTION_TYPES.MULTIPLE_CHOICE, QUESTION_TYPES.AUDIO_MULTI_CHOICE, QUESTION_TYPES.TRUE_FALSE, QUESTION_TYPES.AUDIO_TRUE_FALSE, QUESTION_TYPES.MULTIPLE_RESPONSE, QUESTION_TYPES.MATCH].includes(question.type));
    }
    return false;
  }

  const hasValidAiFeedback = (submissionData) => {
    return true;
    if(typeCheckQuestion(submissionData?.assignment?.questions)) {
      return true;
    }
    const ai_feedback = get(submissionData, 'ai_feedback', []);
    const hasValidAiFeedback = Array.isArray(ai_feedback)
    ? ai_feedback.some((item) => item !== null && item !== undefined)
    : Boolean(ai_feedback);
    return hasValidAiFeedback;
  }

  return {
    getGradeWithPreference,
    getSubmissionStatus,
    isDoneLateSubmission,
    checkIsRetaking,
    isRetakingAvailable,
    getMarkStatusBingo,
    getMarkStatus,
    hasValidAiFeedback,
    typeCheckQuestion 
  };
};
